import React from 'react';
import { Flex, Spin, Typography } from 'antd';
import { ChevronDownIcon, ChevronUpIcon } from '@/assets/icons/icons';
import type { FlatRow } from '../hooks/useInventoryTable';

export type GuardCell = {
  children: React.ReactNode;
  props: { colSpan: number };
} | null;

type SpecialRowRenderersArgs = {
  tableColumnCount: number;
  loadingRowKey: string | null;
  handleRowClick: (row: FlatRow) => Promise<void>;
  toggleGroup: (groupId: string) => void;
  toggleSubGroup: (groupId: string, subgroupId: string) => void;
};

const LoaderCell = () => (
  <div className='inventory-loader-cell'>
    <Spin size='small' />
  </div>
);

export const createSpecialRowRenderers = ({
  tableColumnCount,
  loadingRowKey,
  handleRowClick,
  toggleGroup,
  toggleSubGroup,
}: SpecialRowRenderersArgs) => {
  const renderFullWidthSpecialRow = (record: FlatRow): GuardCell => {
    if (record.rowType === 'loader') {
      return {
        children: <LoaderCell />,
        props: { colSpan: tableColumnCount },
      };
    }

    if (record.rowType === 'load_more') {
      const isRowLoading = loadingRowKey === record.key;

      return {
        children: (
          <div
            className={`inventory-load-more-cell ${
              isRowLoading ? 'is-loading' : ''
            }`}
            onClick={(event) => {
              event.stopPropagation();
              if (!isRowLoading) {
                void handleRowClick(record);
              }
            }}
          >
            {isRowLoading ? 'Loading...' : record.label}
          </div>
        ),
        props: {
          colSpan: tableColumnCount,
        },
      };
    }

    if (record.rowType === 'group') {
      const isSubGroup = record.__depth === 1;
      const showTeamDot = !!record.color;

      return {
        children: (
          <Flex
            align='center'
            gap={10}
            className={`inventory-group-cell ${
              isSubGroup
                ? 'inventory-subgroup-cell'
                : 'inventory-main-group-cell'
            }`}
          >
            <span
              className='inventory-inline-expand-icon'
              onClick={(event) => {
                event.stopPropagation();

                if (record.subgroupId) {
                  void toggleSubGroup(record.groupId, record.subgroupId);
                } else {
                  void toggleGroup(record.groupId);
                }
              }}
            >
              {record.isExpanded ? (
                <ChevronUpIcon size={16} color='var(--text-primary)' />
              ) : (
                <ChevronDownIcon size={16} color='var(--text-primary)' />
              )}
            </span>

            {showTeamDot ? (
              <svg
                className='inventory-group-team-dot'
                viewBox='0 0 10 10'
                aria-hidden='true'
                focusable='false'
              >
                <circle cx='5' cy='5' r='4.5' fill={record.color} />
              </svg>
            ) : null}

            <Typography.Text
              className='inventory-group-label'
              ellipsis={{ tooltip: record.label }}
              strong
            >
              {record.label}
            </Typography.Text>
          </Flex>
        ),
        props: { colSpan: tableColumnCount },
      };
    }

    return null;
  };

  const renderBlankSpecialCell = (record: FlatRow): GuardCell => {
    if (
      record.rowType === 'loader' ||
      record.rowType === 'load_more' ||
      record.rowType === 'group'
    ) {
      return { children: null, props: { colSpan: 0 } };
    }

    return null;
  };

  return {
    renderBlankSpecialCell,
    renderFullWidthSpecialRow,
  };
};
